import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { Calendar as CalendarIcon, MapPin, Users, Plus, Filter, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Events = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const eventTypes = [
    { value: 'all', label: t('events.filters.all') },
    { value: 'concentration', label: t('events.filters.concentration') },
    { value: 'ride', label: t('events.filters.ride') },
    { value: 'meeting', label: t('events.filters.meeting') },
    { value: 'race', label: t('events.filters.race') },
  ];

  const countries = [
    { value: 'all', label: t('events.filters.all') },
    { value: 'FR', label: t('countries.FR') },
    { value: 'DE', label: t('countries.DE') },
    { value: 'ES', label: t('countries.ES') },
    { value: 'IT', label: t('countries.IT') },
    { value: 'BE', label: t('countries.BE') },
    { value: 'CH', label: t('countries.CH') },
  ];

  useEffect(() => {
    fetchEvents();
  }, [typeFilter, countryFilter, dateFilter]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.append('event_type', typeFilter);
      if (countryFilter !== 'all') params.append('country', countryFilter);
      if (dateFilter) params.append('date_from', format(dateFilter, 'yyyy-MM-dd'));
      
      const response = await axios.get(`${API_URL}/events?${params.toString()}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleJoinEvent = async (eventId) => {
    try {
      await axios.post(
        `${API_URL}/events/${eventId}/join`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('common.success'));
      fetchEvents();
    } catch (error) {
      const message = error.response?.data?.detail || t('common.error');
      toast.error(message);
    }
  };

  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, 'PPP', { locale: i18n.language === 'fr' ? fr : enUS });
  };

  const getEventTypeColor = (type) => {
    const colors = {
      concentration: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
      ride: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      meeting: 'bg-green-500/10 text-green-500 border-green-500/20',
      race: 'bg-red-500/10 text-red-500 border-red-500/20',
    };
    return colors[type] || 'bg-muted text-muted-foreground';
  };

  const filteredEvents = events.filter(event => 
    searchQuery === '' || 
    event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    event.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight">
            {t('events.title')}
          </h1>
        </div>
        <Link to="/events/create">
          <Button className="brand-glow font-bold uppercase tracking-wider gap-2" data-testid="create-event-btn">
            <Plus className="w-4 h-4" />
            {t('events.create')}
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 bg-zinc-950/50 border-zinc-800"
            data-testid="search-events-input"
          />
        </div>

        {/* Date Filter */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-12 gap-2 min-w-[180px]" data-testid="date-filter-btn">
              <CalendarIcon className="w-4 h-4" />
              {dateFilter ? format(dateFilter, 'PP', { locale: i18n.language === 'fr' ? fr : enUS }) : 'Date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={dateFilter}
              onSelect={setDateFilter}
              initialFocus
            />
            {dateFilter && (
              <div className="p-2 border-t">
                <Button 
                  variant="ghost" 
                  className="w-full" 
                  onClick={() => setDateFilter(null)}
                  data-testid="clear-date-filter"
                >
                  {t('common.cancel')}
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>

        {/* Type Filter */}
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-12 w-full md:w-[180px]" data-testid="type-filter-select">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            {eventTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Country Filter */}
        <Select value={countryFilter} onValueChange={setCountryFilter}>
          <SelectTrigger className="h-12 w-full md:w-[180px]" data-testid="country-filter-select">
            <SelectValue placeholder={t('profile.country')} />
          </SelectTrigger>
          <SelectContent>
            {countries.map((country) => (
              <SelectItem key={country.value} value={country.value}>
                {country.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Events Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="animate-pulse">
              <div className="h-48 bg-muted" />
              <CardContent className="p-4">
                <div className="h-4 bg-muted rounded w-1/4 mb-2" />
                <div className="h-6 bg-muted rounded w-3/4 mb-4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredEvents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map((event, index) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="group hover:border-primary/50 transition-colors overflow-hidden" data-testid={`event-card-${event.id}`}>
                <div 
                  className="h-48 bg-cover bg-center relative"
                  style={{
                    backgroundImage: event.image_url 
                      ? `url('${event.image_url}')` 
                      : `url('https://images.unsplash.com/photo-1753041419705-b0795c0465ae?crop=entropy&cs=srgb&fm=jpg&q=85')`,
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                  <Badge className={`absolute top-3 right-3 ${getEventTypeColor(event.event_type)}`}>
                    {t(`events.filters.${event.event_type}`)}
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <p className="text-sm text-primary font-mono mb-1">{formatEventDate(event.date)}</p>
                  <h3 className="font-heading font-bold text-xl uppercase tracking-tight line-clamp-1">
                    {event.title}
                  </h3>
                  <p className="text-muted-foreground text-sm line-clamp-2 mt-2">
                    {event.description}
                  </p>
                  <div className="flex items-center gap-4 mt-4 text-muted-foreground text-sm">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span className="line-clamp-1">{event.location}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{event.participants_count}</span>
                    </div>
                  </div>
                  <Button 
                    className="w-full mt-4" 
                    onClick={() => handleJoinEvent(event.id)}
                    data-testid={`join-event-${event.id}`}
                  >
                    {t('events.join')}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      ) : (
        <Card className="bg-card/50">
          <CardContent className="p-12 text-center">
            <CalendarIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl uppercase">{t('events.noEvents')}</h3>
            <p className="text-muted-foreground mt-2">
              {i18n.language === 'fr' 
                ? 'Soyez le premier à créer un événement !' 
                : 'Be the first to create an event!'}
            </p>
            <Link to="/events/create">
              <Button className="mt-6" data-testid="create-event-empty">
                {t('events.create')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Events;
